import { useLazyQuery, useQuery } from "@apollo/client";
import { Loader2, Search, X } from "lucide-react";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PriceDisplay } from "@/components/ui/price-display";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateAppointmentServices } from "@/domains/appointment/appointments/shared/hooks/use-update-appointment-services";
import type { AppointmentServiceWithDetailFragment, Service } from "@/gql/graphql";
import { VALIDATE_BOOKING_CONSTRAINTS_QUERY } from "@/graphql/appointments";
import { SERVICES_QUERY } from "@/graphql/services";
import { useSalonStore } from "@/lib/store/salon";
import { timezoneUtils } from "@/lib/timezone";
import { cn } from "@/lib/utils";

interface EditServicesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  currentStartTime: string;
  appointmentServices: AppointmentServiceWithDetailFragment[] | null | undefined;
}

export function EditServicesDialog({
  open,
  onOpenChange,
  appointmentId,
  currentStartTime,
  appointmentServices,
}: EditServicesDialogProps) {
  const { currentSalon } = useSalonStore();
  const { t: tForms } = useTranslation("forms");
  const { t: tAppointments } = useTranslation("appointments");

  const { data: servicesData, loading: servicesLoading } = useQuery(SERVICES_QUERY, {
    variables: { salonId: currentSalon?.id || "" },
    skip: !currentSalon?.id,
  });

  const { updateServices, isUpdating } = useUpdateAppointmentServices();

  const [localServices, setLocalServices] = useState<AppointmentServiceWithDetailFragment[]>(() =>
    (appointmentServices || []).map((s) => ({ ...s }))
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [validationWarning, setValidationWarning] = useState<string>("");
  const [validateConstraints] = useLazyQuery(VALIDATE_BOOKING_CONSTRAINTS_QUERY);

  // keep localServices in sync when appointmentServices prop changes
  useEffect(() => {
    setLocalServices((appointmentServices || []).map((s) => ({ ...s })));
  }, [appointmentServices]);

  const services = servicesData?.services || [];

  // Get available categories
  const categories = useMemo(() => {
    const cats = ["All"];
    const uniqueCategories = new Set(
      services.map((s) => s.category?.name).filter((n): n is string => !!n)
    );
    cats.push(...Array.from(uniqueCategories));
    return cats;
  }, [services]);

  // Filter services based on search and category
  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "All" || service.category?.name === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [services, searchTerm, selectedCategory]);

  const totalDuration = localServices.reduce((sum, s) => sum + (s.serviceDuration || 0), 0);
  const totalPrice = localServices.reduce((sum, s) => sum + (s.totalPrice || 0), 0);

  const isServiceSelected = (serviceId: string): boolean => {
    return localServices.some((s) => s.id === serviceId || s.serviceId === serviceId);
  };

  const handleServiceToggle = (service: Service) => {
    const isSelected = isServiceSelected(service.id);

    if (isSelected) {
      // Remove service
      setLocalServices((prev) => {
        const next = prev.filter((s) => s.id !== service.id && s.serviceId !== service.id);
        return next;
      });
    } else {
      // Add service as temporary AppointmentServiceWithDetailFragment
      const newService: AppointmentServiceWithDetailFragment = {
        __typename: "AppointmentService",
        id: `new-${Date.now()}`,
        serviceName: service.name,
        serviceDuration: service.duration || 0,
        totalPrice: service.basePrice || 0,
        basePrice: service.basePrice || 0,
        optionsTotal: 0,
        pricingMode: service.pricingMode,
        sequenceOrder: (localServices.length || 0) + 1,
        service: service,
        serviceId: service.id,
        workerId: null,
        worker: null,
        serviceCategoryName: service.category?.name ?? "",
        selectedOptions: [],
        serviceDescription: service.description,
      };
      setLocalServices((prev) => [...prev, newService]);
    }
  };

  // Validate booking constraints (non-blocking, just shows warning)
  const validateBooking = useCallback(async () => {
    try {
      const start = timezoneUtils.fromISO(currentStartTime);
      const end = start.plus({ minutes: totalDuration });

      const res = await validateConstraints({
        variables: {
          input: {
            salonId: currentSalon?.id ?? "",
            startTime: start.toISO() ?? "",
            endTime: end.toISO() ?? "",
          },
        },
      });

      const validation = res.data?.validateBookingConstraints;
      if (!validation?.isValid) {
        setValidationWarning(validation?.errors?.[0] || tForms("descriptions.overrideWarning"));
      } else {
        setValidationWarning("");
      }
    } catch (_e) {
      // Silently fail validation check - don't block user
      setValidationWarning("");
    }
  }, [currentStartTime, totalDuration, validateConstraints, currentSalon?.id, tForms]);

  // Validate on dialog open and when services change
  useEffect(() => {
    if (open && localServices.length > 0) {
      validateBooking();
    }
  }, [open, localServices.length, validateBooking]);

  const handleSave = async () => {
    if ((localServices?.length || 0) === 0) {
      return; // Don't save if no services
    }

    // Build complete desired state for UpsertService
    // UpsertService will automatically detect creates, updates, and deletes
    const services = localServices.map((s) => ({
      // Include id only if it exists and is not a temporary "new-" ID (used for React keys)
      id: s.id && !s.id.toString().startsWith("new-") ? s.id : undefined,
      serviceId: s.service?.id ?? s.serviceId ?? "",
      workerId: s.workerId ?? null,
      selectedOptionIds: [],
    }));

    const result = await updateServices(appointmentId, {
      services,
    });

    // Close dialog on success
    if (result) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{tForms("titles.editServices")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Services Summary */}
          <div>
            <div className="flex flex-wrap gap-2 min-h-[32px] items-start">
              {localServices.length > 0 ? (
                localServices.map((service) => (
                  <Badge
                    key={service.id}
                    variant="secondary"
                    className="text-xs flex items-center gap-1"
                  >
                    {service.serviceName}
                    {(service.pricingMode !== "NO_PRICE" || service.totalPrice > 0) && (
                      <>
                        {" "}
                        - <PriceDisplay amount={service.totalPrice} />
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setLocalServices((prev) => prev.filter((s) => s.id !== service.id));
                      }}
                      className="ml-1 hover:bg-accent rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-muted-foreground py-1">
                  {tForms("placeholders.selectService")}
                </span>
              )}
            </div>
          </div>

          {/* Search and Category Filter */}
          <div>
            <h3 className="text-sm font-semibold mb-3">{tForms("titles.availibleServices")}</h3>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder={tForms("placeholders.search")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="hidden sm:block">
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category} value={category}>
                          {category === "All"
                            ? tAppointments("booking.servicesStep.categories.all")
                            : category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Services List with Checkboxes */}
          <div className="border rounded-lg max-h-64 overflow-y-auto bg-card scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent">
            {servicesLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>{tAppointments("messages.loadingServices")}</span>
              </div>
            ) : filteredServices.length === 0 ? (
              <div className="text-center p-8 text-muted-foreground">
                {searchTerm || selectedCategory !== "All"
                  ? tAppointments("booking.servicesStep.messages.noServicesFound")
                  : tAppointments("booking.servicesStep.messages.noServicesAvailable")}
              </div>
            ) : (
              <div className="divide-y">
                {filteredServices.map((service) => {
                  const isSelected = isServiceSelected(service.id);
                  return (
                    <button
                      key={service.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={0}
                      className={cn(
                        "w-full text-left p-4 hover:bg-accent cursor-pointer transition-colors",
                        isSelected && "bg-accent border-l-4 border-l-primary"
                      )}
                      onClick={() => handleServiceToggle(service as Service)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleServiceToggle(service as Service)}
                            className="pointer-events-none"
                          />
                          <div>
                            <div className="font-medium text-sm">{service.name}</div>
                            <div className="text-xs text-muted-foreground flex items-center gap-3">
                              {service.pricingMode === "NO_PRICE" ? (
                                <span className="text-amber-600 dark:text-amber-400">
                                  {tAppointments("details.labels.quoteRequired")}
                                </span>
                              ) : (
                                <PriceDisplay amount={service.basePrice ?? 0} />
                              )}
                              {service.category?.name && (
                                <Badge variant="outline" className="text-xs">
                                  {service.category.name}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="pt-4 border-t space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {tAppointments("booking.servicesStep.totalDuration")}
              </span>
              <span className="font-medium">
                {totalDuration} {tAppointments("details.labels.minutes")}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {tAppointments("booking.servicesStep.estimatedEnd")}
              </span>
              <span className="font-medium">
                {timezoneUtils
                  .fromISO(currentStartTime)
                  .plus({ minutes: totalDuration })
                  .toLocaleString(DateTime.DATETIME_MED)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {tAppointments("booking.servicesStep.totalPrice")}
              </span>
              <span className="font-medium">
                <PriceDisplay amount={totalPrice} />
              </span>
            </div>
          </div>

          {validationWarning && (
            <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <svg
                  className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="text-sm text-amber-900 dark:text-amber-100">
                  {validationWarning}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
            {tForms("actions.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isUpdating || (localServices?.length || 0) === 0}>
            {tForms("actions.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
